import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { endOfToday, format, startOfToday } from 'date-fns';

import { periodFromDate } from '@ecp/common/src/utils/dateUtils';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import { useDescriptionContentUtils } from '@ecp/common/src/utils/useDescriptionContentUtils';
import { metaDescriptionContent, documentTitle } from '@ecp/common/src/const/htmlHeadMessage';

import { downloadFiles } from '@bo-utils/fileUtils';
import { useDashboardGridOptions } from '@bo-hooks/dashBoard/useDashboard';
import { getPoNotificationList } from '@bo-apis/po/customerService/poNotificationApi';
import poDeliveryApi from '@bo-apis/po/delivery/poDeliveryApi';
import commonApi from '@bo-apis/common/commonApi';
import DashboardContentBox from '@bo-components/dashBoard/DashboardContentBox';
import Grid from '@bo-components/common/grid/Grid';
import ManualDownloadCard from '@bo-components/dashBoard/ManualDownloadCard';
import NoticePopup from '@bo-components/dashBoard/NoticePopup';
import DeliverySummary from '@bo-components/dashBoard/DeliverySummary';
import { ReactComponent as RefreshIcon } from '@bo-assets/icon/common/icon__refresh.svg';
import WelmallLogo from '@bo-assets/image/common/image__ci_logo.png';

const PoMainPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);
  const [showNoticePopup, setShowNoticePopup] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState('');
  const [deliverySummaryList, setDeliverySummaryList] = useState([...initSummaryList]);

  /*PO html head meta description content*/
  useDescriptionContentUtils(metaDescriptionContent.poContent);
  useLayoutEffect(() => {
    document.title = documentTitle.poTitle;
  }, []);

  const { gridOptions } = useDashboardGridOptions();

  const getRefreshSummary = useCallback(async () => {
    const searchForm = {
      periodTypeCode: 'deliDirDate',
      fromDate: format(periodFromDate({ months: 1 }), 'yyyyMMddHHmmss'),
      toDate: format(endOfToday(), 'yyyyMMddHHmmss'),
    };

    const {
      result: { orderConfirm, shipDirection, shipComplete },
    } = await poDeliveryApi.getDeliveryCount({ searchForm });

    setDeliverySummaryList((prev) => {
      const tmpSummary = [...prev];
      tmpSummary[0].cnt = orderConfirm;
      tmpSummary[1].cnt = shipDirection;
      tmpSummary[2].cnt = shipComplete;
      return tmpSummary;
    });
  }, []);

  useEffect(() => {
    (async () => await getRefreshSummary())();
  }, [getRefreshSummary]);

  const handleSearch = useCallback(
    async (pageIndex, pageSize, sort = {}) => {
      const params = {
        fromDateTime: format(startOfToday(), 'yyyyMMddHHmmss'),
        toDateTime: format(endOfToday(), 'yyyyMMddHHmmss'),
        title: '',
      };
      const {
        result: { notificationList, counts },
      } = await getPoNotificationList({
        searchForm: params,
        pageable: { size: pageSize, page: pageIndex, sort },
      });
      setContent(notificationList.content);
      setTotal(counts);
    },
    [setContent, setTotal]
  );

  const handleRowClick = useCallback(
    ({ row }) => {
      if (row?.Kind !== 'Header') {
        setShowNoticePopup(true);
        setSelectedNotice(row.notificationNumber.toString());
      }
    },
    [setShowNoticePopup]
  );

  const handleClickDownload = async (id) => {
    const { result } = await commonApi.getFileByFileId(id);
    await downloadFiles(result);
  };

  const handleSummaryClick = (stateFilterIdx) => {
    navigate('/partner/delivery/management', { state: { stateFilterIdx } });
  };

  return (
    <>
      <Container>
        <img src={WelmallLogo} alt={'logo'} />
        <Spacing bottom={12} />
        <TextBox font-weight={'700'} font-size={24}>
          Dashboard
        </TextBox>
        <Spacing bottom={60} />
        <FlexBox gap={'6px'}>
          <TextBox font-weight={'400'} font-size={14} color={'#333333'}>
            {format(periodFromDate({ months: 1 }), 'yyyy-MM-dd')}
          </TextBox>
          <TextBox>~</TextBox>
          <TextBox font-weight={'400'} font-size={14} color={'#333333'}>
            {format(startOfToday(), 'yyyy-MM-dd')}
          </TextBox>
          <RefreshButton onClick={getRefreshSummary}>
            <RefreshIcon />
          </RefreshButton>
        </FlexBox>
        <Spacing bottom={14} />
        <FlexBox gap={'16px'}>
          {deliverySummaryList.map(({ label, cnt, stateFilterIdx }, idx) => (
            <DeliverySummary label={label} cnt={cnt} key={idx} onClick={() => handleSummaryClick(stateFilterIdx)} />
          ))}
        </FlexBox>
        <Spacing bottom={20} />
        <FlexBox>
          <DashboardContentBox title={'공지사항'} width={'728px'} bodyHeight={'450px'}>
            <Grid
              unPaged
              readOnly
              total={total}
              data={content}
              options={gridOptions}
              onSearch={handleSearch}
              onClick={handleRowClick}
              height={'300px'}
            />
          </DashboardContentBox>
          <Spacing left={16} />
          <DashboardContentBox title={'매뉴얼 다운로드'} width={'356px'} bodyHeight={'450px'}>
            <ManualDownloadCard label={'PO 매뉴얼'} onClick={() => handleClickDownload('po_manual_file')} />
            <ManualDownloadCard
              label={'상품일괄등록 가이드'}
              onClick={() => handleClickDownload('good_regist_manual_file')}
            />
            <ManualDownloadCard
              label={'가격정보엑셀업로드 가이드'}
              onClick={() => handleClickDownload('price_info_manual_file')}
            />
            <ManualDownloadCard
              label={'옵션정보엑셀 가이드'}
              onClick={() => handleClickDownload('option_info_manual_file')}
            />
          </DashboardContentBox>
        </FlexBox>
      </Container>
      <NoticePopup
        isBO={false}
        open={showNoticePopup}
        onClose={() => setShowNoticePopup(false)}
        noticeSeq={selectedNotice}
      />
    </>
  );
};

export default PoMainPage;

const Container = styled(FlexBoxColumn)`
  padding: 44px 30px 0;
  align-items: flex-start;
`;

const RefreshButton = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

const initSummaryList = [
  { label: '주문확인처리', cnt: 0, stateFilterIdx: 2 },
  { label: '상품준비중', cnt: 0, stateFilterIdx: 3 },
  { label: '출하완료', cnt: 0, stateFilterIdx: 4 },
];
